/**
 * Quick add to cart for product cards (snippets/card.liquid).
 *
 * This module owns ALL quick-add behaviour for cards: click handling, the
 * add-to-cart request, the minicart open, and the mobile size-picker drawer
 * (snippets/quick-add-drawer.liquid). It is fully decoupled from the wishlist.
 *
 * Card controls live inside the card's <a href>, so every click in the
 * quick-add zone is preventDefault-ed here to stop navigation to the PDP.
 *
 * Wishlist integration is event-based: on a successful add we publish
 * PUB_SUB_EVENTS.cartUpdate with source 'quick-add'. wishlist.js listens and
 * removes the product from the wishlist (remove-on-add) when applicable.
 *
 * Un contexto puede traer su propia sheet de tallas y quedarse solo con el alta:
 * las cards de dentro del drawer de "Shop the look" abren la sheet de aquel —la
 * misma del banner, con las fotos, el título y el precio del producto— y luego
 * vuelven aquí por addToCart (window.quickAddCart), de modo que el carrito, el
 * contador de la cabecera y el minicart se comportan igual desde cualquier card
 * del sitio. Ver openLookSheet más abajo y assets/shop-the-look.js.
 *
 * Globals consumed (all defined before this script runs):
 *   fetchConfig (base.js), publish / PUB_SUB_EVENTS (pubsub.js / constants.js),
 *   window.routes, window.variantStrings, window.cartStrings (head-init.liquid),
 *   window.OptionTypeUtils / window.CartAddSections (global.js),
 *   window.minicart (minicart.js),
 *   window.sizeChartHelpers (size-chart.js).
 */
(function () {
  const SELECTORS = {
    card: '.brn-card',
    // Any element inside a card whose click must never navigate to the PDP.
    zone: '.add__to__cart, .add__to__cart--desktop, .variants-container, .variant-elem',
    addButton: '.add__to__cart',
    variantElem: '.variant-elem',
    drawer: '#quick-add-drawer',
    drawerHandle: '#quick-add-drawer .quick-add-drawer__handle',
    drawerList: '#quick-add-drawer .quick-add-drawer__content ul',
    drawerItem: '#quick-add-drawer li',
    overlay: '.quick-add-overlay',
    sizeGuideTarget: '.quick-add-drawer__size-guide',
    sizingTarget: '.quick-add-drawer__sizing',
  };

  // Size-guide HTML cache keyed by product handle (shared by prefetch + open).
  const sizeGuideCache = new Map();

  // Scroll position saved on open and restored on close (see showDrawer/closeDrawer).
  let lockedScrollY = 0;

  // Id propio para el modal de la guía que se inyecta en la sheet: el que trae
  // la PDP es el del bloque y choca con el de la ficha en la que se esté.
  const GUIDE_MODAL_ID = 'quick-add-size-guide';

  function init() {
    document.addEventListener('click', onClick);
    document.addEventListener('mouseover', onVariantHover);
    document.addEventListener('mouseout', onVariantHoverOut);
    initSizeGuidePrefetch();
    initDragToClose();
  }

  // ---------------------------------------------------------------------------
  // Click routing
  // ---------------------------------------------------------------------------
  function onClick(e) {
    // Drawer close affordances.
    if (e.target.closest(SELECTORS.overlay)) {
      e.preventDefault();
      closeDrawer();
      return;
    }
    if (e.target.closest(SELECTORS.drawerHandle)) {
      closeDrawer();
      return;
    }

    // Coming-soon "Avísame" opener (card CTA, desktop pill, drawer row, or the
    // whole desktop hover panel when the card is entirely out of stock).
    // Stop navigation to the PDP and let <modal-opener> + the global coming-soon
    // handler (cart-drawer.js) show/populate the modal. Must run before the zone
    // checks because the tablet/mobile CTA lives outside the quick-add zone.
    const notifyOpener = e.target.closest('.brn-coming-soon, .add__to__cart--desktop--coming-soon');
    if (notifyOpener) {
      e.preventDefault();
      const link = notifyOpener.querySelector('.brn-coming-soon__link');
      populateComingSoonModal(link);
      // <modal-opener>'s own listener only fires for clicks that land on its
      // button; open explicitly so clicks anywhere in the wider hover panel work too.
      const modal = document.getElementById('QuickAddComingSoonModal');
      if (modal && typeof modal.show === 'function') modal.show(link);
      closeDrawer();
      return;
    }

    // Size tapped inside the mobile drawer.
    const drawerItem = e.target.closest(SELECTORS.drawerItem);
    if (drawerItem) {
      e.preventDefault();
      if (drawerItem.getAttribute('aria-available') === 'true') {
        addToCart(drawerItem.dataset.variantId, { productId: drawerItem.dataset.productId });
        closeDrawer();
      }
      return;
    }

    // Card quick-add controls. Anything in the zone must not follow the link.
    const zone = e.target.closest(SELECTORS.zone);
    if (!zone || !zone.closest(SELECTORS.card)) return;
    e.preventDefault();

    // Inline size button (desktop hover panel).
    const variantElem = e.target.closest(SELECTORS.variantElem);
    if (variantElem) {
      if (variantElem.getAttribute('aria-available') === 'true') {
        addToCart(variantElem.dataset.variantId, { productId: variantElem.dataset.productId });
      } else if (variantElem.classList.contains('variant-elem--coming-soon')) {
        openComingSoonForVariant(variantElem);
      }
      return;
    }

    // Add button (tablet "+" / mobile row / desktop-single). Clicks that land on
    // the .add__to__cart--desktop wrapper padding fall through here as a no-op
    // (navigation already prevented above), which is the desired behaviour.
    const addBtn = e.target.closest(SELECTORS.addButton);
    if (!addBtn) return;

    if (addBtn.dataset.single === 'true') {
      addToCart(addBtn.dataset.firstVariant, { productId: addBtn.dataset.productId });
    } else if (!openLookSheet(addBtn)) {
      openDrawerForProduct(addBtn.dataset.productId, addBtn.dataset.productHandle, {
        comingSoon: addBtn.dataset.comingSoon === 'true',
        productImage: addBtn.dataset.productImage || '',
      });
    }
  }

  // "Shop the look" tiene su propia sheet de tallas y es la que abren las cards
  // que están dentro de su drawer, en lugar de esta. Devuelve false —y sigue la
  // sheet global— si el look no trae bloque para ese producto, para no dejar la
  // card sin respuesta.
  function openLookSheet(addBtn) {
    const lookDrawer = addBtn.closest('shop-the-look-drawer');
    if (typeof lookDrawer?.openSizeSheet !== 'function') return false;
    return lookDrawer.openSizeSheet(addBtn.dataset.productHandle);
  }

  // The "Avísame" mail icon is authored in Liquid, in
  // snippets/quick-add-drawer.liquid, so every context uses the same glyph.
  let mailIconCache = null;
  function mailIcon() {
    if (mailIconCache === null) {
      mailIconCache =
        document.getElementById('quick-add-mail-icon-template')?.innerHTML.trim() || '';
    }
    return mailIconCache;
  }

  // Build an "Avísame" opener targeting the global coming-soon modal. Icon only —
  // the label lives on aria-label, matching the desktop size pills.
  function notifyLinkMarkup(variantId, productId, image) {
    const label = window.variantStrings?.notifyCard || 'Avísame';
    return (
      `<modal-opener class="brn-coming-soon brn-coming-soon--drawer" data-modal="#QuickAddComingSoonModal">` +
      `<button type="button" class="brn-coming-soon__link" data-variant-id="${variantId}" data-product-id="${productId}" data-product-image="${image || ''}" aria-label="${label}">` +
      `${mailIcon()}</button></modal-opener>`
    );
  }

  // Fill the global coming-soon modal with the clicked product's id + image, and
  // reset it to the request state (it is reused across products). variant_id is
  // bound by cart-drawer.js's coming-soon handler.
  function populateComingSoonModal(link) {
    const modal = document.getElementById('QuickAddComingSoonModal');
    if (!modal || !link) return;

    const form = modal.querySelector('.brn-coming-soon__form');
    const productInput = form?.querySelector('input[name="product_id"]');
    if (productInput) productInput.value = link.dataset.productId || '';

    const img = modal.querySelector('[data-coming-soon-image]');
    const src = link.dataset.productImage || '';
    if (img) {
      // Hide immediately so the previous product's image isn't shown while
      // the new one loads; only reveal once the new image is ready.
      img.hidden = true;
      img.removeAttribute('src');
      if (src) {
        img.onload = () => {
          img.hidden = false;
        };
        img.src = src;
      }
    }

    const request = modal.querySelector('[data-coming-soon-request]');
    const success = modal.querySelector('[data-coming-soon-success]');
    if (request) request.hidden = false;
    if (success) success.hidden = true;
    const email = form?.querySelector('input[type="email"]');
    if (email) email.value = '';
  }

  // Open the global coming-soon modal for a desktop size pill. Unlike the
  // <modal-opener> CTAs, the pill isn't a modal-opener and isn't a
  // .brn-coming-soon__link, so we populate, bind the variant_id (cart-drawer.js's
  // binding won't fire here) and open the modal ourselves.
  function openComingSoonForVariant(el) {
    const modal = document.getElementById('QuickAddComingSoonModal');
    if (!modal || typeof modal.show !== 'function') return;
    populateComingSoonModal(el);
    const variantInput = modal.querySelector('.brn-coming-soon__form input[name="variant_id"]');
    if (variantInput) variantInput.value = el.dataset.variantId || '';
    modal.show(el);
  }

  // Swap the desktop panel label to the coming-soon CTA while hovering an unavailable size.
  function onVariantHover(e) {
    const el = e.target.closest('.brn-card .variant-elem');
    if (!el || el.getAttribute('aria-available') !== 'false') return;
    const label = el.closest('.add__to__cart--desktop')?.querySelector('p');
    if (label) label.textContent = window.variantStrings?.notifyCard || 'Coming soon. ¡Avísame!';
  }

  function onVariantHoverOut(e) {
    const el = e.target.closest('.brn-card .variant-elem');
    if (!el || el.getAttribute('aria-available') !== 'false') return;
    const label = el.closest('.add__to__cart--desktop')?.querySelector('p');
    if (label) label.textContent = window.variantStrings?.addToCart || 'Añadir a la cesta';
  }

  // ---------------------------------------------------------------------------
  // Mobile size drawer
  // ---------------------------------------------------------------------------
  // Inline path (in-card controls): sizes come from the `variants-<id>` JSON.
  // opts.comingSoon / opts.productImage drive the per-size "Avísame" rows.
  function openDrawerForProduct(productId, productHandle, opts = {}) {
    if (!document.querySelector(SELECTORS.drawer)) return;
    populateSizes(productId, opts);
    if (productHandle) loadSizeGuide(productHandle);
    showDrawer();
  }

  // Fetch path (<quick-add> element): sizes come from a fetched product payload.
  function openDrawerFromProduct(product) {
    if (!document.querySelector(SELECTORS.drawer)) return;
    populateSizesFromProduct(product);
    if (product.handle) loadSizeGuide(product.handle);
    showDrawer();
  }

  function showDrawer() {
    const drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer) return;
    drawer.setAttribute('aria-expanded', 'true');
    drawer.setAttribute('cart-process', 'open');
    document.querySelector(SELECTORS.overlay)?.setAttribute('aria-overlay', 'open');

    // body.modal-open switches to position:fixed to lock scroll, which
    // otherwise snaps the page to scrollTop 0. Pin body at its current
    // offset via `top` so the underlying page stays put while locked.
    lockedScrollY = window.scrollY;
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.classList.add('modal-open');
  }

  function closeDrawer() {
    document.querySelector(SELECTORS.drawer)?.setAttribute('aria-expanded', 'false');
    document.querySelector(SELECTORS.overlay)?.setAttribute('aria-overlay', 'close');

    document.body.classList.remove('modal-open');
    document.body.style.top = '';
    window.scrollTo(0, lockedScrollY);
  }

  // Arrastrar la sheet hacia abajo para cerrarla. Hasta ahora el único cierre
  // era tocar el velo: el asa se oculta mientras cart-process="open", y en una
  // bottom sheet el gesto que se espera es este. Mismo patrón que el sheet de
  // tallas de la PDP (MobileSizeModal.setupDragEvents, en
  // snippets/component-sheet-size.liquid).
  const DRAG_CLOSE_PX = 80;

  function initDragToClose() {
    const drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer) return;

    let startY = 0;
    let dragging = false;
    const isOpen = () => drawer.getAttribute('aria-expanded') === 'true';

    drawer.addEventListener(
      'touchstart',
      (e) => {
        if (!isOpen()) return;
        startY = e.touches[0].clientY;
        dragging = false;
      },
      { passive: true }
    );

    drawer.addEventListener(
      'touchmove',
      (e) => {
        if (!isOpen()) return;
        const dy = e.touches[0].clientY - startY;

        if (!dragging) {
          // Solo se toma el gesto si la lista está arriba del todo y el
          // movimiento va claramente hacia abajo; si no, scroll nativo.
          if (drawer.scrollTop <= 0 && dy > 8) {
            dragging = true;
            drawer.style.transition = 'none';
          } else {
            return;
          }
        }

        e.preventDefault();
        if (dy > 0) drawer.style.transform = `translateY(${dy}px)`;
      },
      { passive: false }
    );

    const endDrag = (e) => {
      if (!dragging) return;
      dragging = false;

      const dy = (e.changedTouches?.[0]?.clientY ?? startY) - startY;
      // Devolver la transición antes de soltar el transform: sin recorrido
      // suficiente vuelve a su sitio animada, y con él acompaña al cierre.
      drawer.style.transition = '';
      drawer.style.transform = '';
      if (dy > DRAG_CLOSE_PX) closeDrawer();
    };

    drawer.addEventListener('touchend', endDrag);
    drawer.addEventListener('touchcancel', endDrag);
  }

  // The "last units" marker (clock icon + label) is authored in Liquid, in
  // snippets/quick-add-drawer.liquid, so the icon has a single definition.
  let lastUnitsMarkupCache = null;
  function lastUnitsMarkup() {
    if (lastUnitsMarkupCache === null) {
      lastUnitsMarkupCache =
        document.getElementById('quick-add-last-units-template')?.innerHTML.trim() || '';
    }
    return lastUnitsMarkupCache;
  }

  // Render the drawer size list from a normalized list of
  // { id, title, available, lowStock, productId } items.
  // opts.comingSoon → out-of-stock sizes show an "Avísame" link beside them.
  function renderSizeItems(items, opts = {}) {
    const list = document.querySelector(SELECTORS.drawerList);
    if (!list) return;

    list.innerHTML = items
      .filter((item) => item.title)
      .map((item) => {
        const base = `aria-available="${item.available}" data-product-id="${item.productId}" data-variant-id="${item.id}"`;
        if (!item.available && opts.comingSoon) {
          return `<li ${base} class="brn-coming-soon-item"><span>${item.title}</span>${notifyLinkMarkup(item.id, item.productId, opts.productImage)}</li>`;
        }
        // Out-of-stock sizes carry no label: greyed out and non-clickable is the
        // whole signal, same as the desktop size pills.
        const lowStock = item.available && item.lowStock;
        const cls = lowStock ? ' class="quick-add-drawer__item--low-stock"' : '';
        const marker = lowStock ? lastUnitsMarkup() : '';
        return `<li ${base}${cls}><span>${item.title}</span>${marker}</li>`;
      })
      .join('');
  }

  // Inline path: read the `variants-<id>` JSON emitted by card.liquid.
  function populateSizes(productId, opts = {}) {
    const json = document.getElementById(`variants-${productId}`)?.textContent;
    if (!json) return;

    let variants;
    try {
      variants = JSON.parse(json).reverse();
    } catch {
      return;
    }

    renderSizeItems(
      variants.map((variant) => {
        const size = variant.selectedOptions?.find((opt) => window.OptionTypeUtils?.isSize(opt.name));
        return {
          id: variant.id.split('/').pop(),
          title: size ? size.value : variant.title,
          available: variant.availableForSale,
          productId,
        };
      }),
      opts
    );
  }

  // Fetch path (used by the <quick-add> element): build the list from the
  // /products/<handle>.js payload. Variants are size-only in this theme, so the
  // size option value is the variant label.
  function populateSizesFromProduct(product) {
    const optionNames = (product.options || []).map((opt) => (typeof opt === 'string' ? opt : opt.name));
    const sizeIndex = optionNames.findIndex((name) => window.OptionTypeUtils?.isSize(name));

    renderSizeItems(
      (product.variants || []).map((variant) => ({
        id: variant.id,
        title: sizeIndex >= 0 ? variant.options[sizeIndex] : variant.title,
        available: variant.available,
        productId: product.id,
      }))
    );
  }

  // ---------------------------------------------------------------------------
  // Size guide (fetched from the product page, same approach as before)
  // ---------------------------------------------------------------------------
  function initSizeGuidePrefetch() {
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const btn = entry.target;
          if (btn.dataset.single === 'false' && btn.dataset.productHandle) {
            prefetchSizeGuide(btn.dataset.productHandle);
          }
          observer.unobserve(btn);
        });
      },
      { rootMargin: '50px' }
    );
    document
      .querySelectorAll('.brn-card .add__to__cart[data-single="false"][data-product-handle]')
      .forEach((btn) => observer.observe(btn));
  }

  async function prefetchSizeGuide(handle) {
    if (sizeGuideCache.has(handle)) return;
    try {
      const res = await fetch(`/products/${handle}`);
      if (res.ok) sizeGuideCache.set(handle, await res.text());
    } catch {
      /* best-effort prefetch */
    }
  }

  // "Información sobre las tallas": el opener y su modal son las dos piezas que
  // emite snippets/size-guide.liquid en la PDP del producto. Se recortaban por
  // su antiguo envoltorio #size_guide_container, que el rediseño de la PDP
  // eliminó, así que el pie de la sheet se quedaba vacío.
  async function loadSizeGuide(handle) {
    try {
      let html = sizeGuideCache.get(handle);
      if (!html) {
        const res = await fetch(`/products/${handle}`);
        if (!res.ok) return;
        html = await res.text();
        sizeGuideCache.set(handle, html);
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const target = document.querySelector(SELECTORS.sizeGuideTarget);
      if (!target) return;

      // Por .size-guide-btn y no por [data-size-guide-trigger]: ese atributo lo
      // lleva también el enlace del sheet de tallas de la PDP.
      const opener = doc.querySelector('.size-guide-btn')?.closest('modal-opener');
      const modal = doc.querySelector('modal-dialog.brn-modal--size-guide');
      if (opener && modal) {
        // La sheet es única y se reutiliza para cada producto, pero el modal ya
        // inyectado no vive aquí: <modal-dialog> se muda a <body> al conectarse
        // (assets/base.js). Sin retirarlo se acumularían modales y el opener
        // abriría el del producto anterior.
        document.querySelectorAll('modal-dialog[data-quick-add-guide]').forEach((el) => el.remove());

        // Se monta suelto para poder retocarlo ANTES de que se conecte y se mude.
        const holder = document.createElement('div');
        holder.innerHTML = `${opener.outerHTML}${modal.outerHTML}`;
        holder.querySelectorAll('modal-dialog').forEach((el) => {
          el.setAttribute('data-quick-add-guide', '');
          // El id viene de la PDP y es el del bloque, el MISMO que el de la guía
          // de la ficha en la que se esté: modal-opener resuelve data-modal con
          // document.querySelector y abría aquella en vez de esta.
          el.id = GUIDE_MODAL_ID;
          // El hueco del widget de NaizFit se queda fuera: size-chart.js mueve
          // ahí el #naiz-opener-container de la página, que es único, y al
          // retirar este modal en la siguiente apertura se lo llevaría por
          // delante. En su lugar queda el CTA estático que ya trae el pie.
          el.querySelectorAll('[data-naizfit-ui]').forEach((slot) => slot.remove());
        });
        holder
          .querySelectorAll('modal-opener')
          .forEach((el) => el.setAttribute('data-modal', `#${GUIDE_MODAL_ID}`));
        target.replaceChildren(...holder.childNodes);

        // El disparador es nuevo en cada apertura, así que hay que volver a
        // engancharle la animación del diagrama (marca los ya enganchados).
        window.sizeChartHelpers?.initSizeGuideDiagramAnimation?.();
      }

      // El de la ficha, no el aviso de "sin devolución" de buy-buttons.liquid ni
      // el del sheet de tallas, que comparten clase.
      const sizingSource =
        doc.querySelector('.brn-product__info .brn-product__sizing:not(.no_returns-sizing)') ||
        doc.querySelector('.brn-product__sizing:not(.no_returns-sizing)');
      const sizingTarget = document.querySelector(SELECTORS.sizingTarget);
      if (sizingTarget) sizingTarget.innerHTML = sizingSource ? sizingSource.innerHTML : '';

      // La tabla la construye size-chart.js desde el [data-size-guide-source]
      // que viaja dentro del modal. Se le pasa como ámbito: por defecto usa el
      // primer .size-chart del documento, que con la cesta llena es el del
      // sheet de tallas de un artículo del carrito (snippets/cart-item.liquid).
      const liveModal = document.querySelector('modal-dialog[data-quick-add-guide]');
      if (window.sizeChartHelpers && liveModal) {
        window.sizeChartHelpers.initSizeChartTabsFromSource(liveModal);
        window.sizeChartHelpers.buildSizeChartPanesFromSource(liveModal);
        window.sizeChartHelpers.initSizeChartTabs(liveModal);
      }
    } catch (error) {
      console.error('[quick-add] size guide error:', error);
    }
  }

  // ---------------------------------------------------------------------------
  // Add to cart
  // ---------------------------------------------------------------------------
  async function addToCart(variantId, ctx = {}) {
    if (!variantId) return;

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);


    window.CartAddSections.attach(formData);
    config.body = formData;

    try {
      const response = await fetch(`${window.routes.cart_add_url}.js`, config);
      const data = await response.json();

      if (!response.ok || data.status) {
        console.error('[quick-add] add to cart failed:', data?.description || data?.message || response.status);
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'quick-add',
          productVariantId: variantId,
          errors: data?.description,
          message: data?.message,
        });
        // Single-variant cards have no in-card stock cue, so surface failures
        // (almost always sold out — 422) through the unified toaster.
        const soldOut = response.status === 422;
        window.toast?.show(
          soldOut ? window.variantStrings?.soldOut || 'Agotado' : window.cartStrings?.error || data?.description || 'Error',
          { type: 'error', icon: soldOut ? 'info' : 'error' }
        );
        return;
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'quick-add',
        productVariantId: variantId,
        productId: ctx.productId,
        cartData: data,
      });

      openMinicart(data, window.CartAddSections.applyBubble(data));
      window.CartAddSections.refreshDrawer();
    } catch (error) {
      console.error('[quick-add] add to cart error:', error);
    }
  }

  function openMinicart(data, itemCount) {
    if (!window.minicart || typeof window.minicart.open !== 'function') return;

    const lang = document.documentElement.lang || 'es-ES';
    const locale = lang.toLowerCase().includes('es') ? 'es-ES' : lang;
    const priceCents = data.final_price ?? data.price ?? 0;
    const priceText = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(priceCents / 100);

    const selectedSize = data.options_with_values?.find((o) => /size|talla/i.test(o?.name || ''))?.value;
    const title = selectedSize
      ? window.cartStrings.addedToCartSize.replace('__size__', selectedSize)
      : window.cartStrings.addedToCart;

    window.minicart.open({
      title,
      productTitle: data.product_title,
      imageSrc: data.image,
      imageAlt: data.title,
      priceText,
      variantText: data.variant_title,
      itemCount,
    });
  }

  // ---------------------------------------------------------------------------
  // <quick-add> custom element
  //
  // The trigger used outside product cards (upsells, shoppable, collection-list
  // cards). It wraps a button and delegates to the same engine: resolve the
  // product from /products/<handle>.js, then add directly (single variant) or
  // open the size drawer (multi). Replaces the old quick-view (quick-add.js).
  // ---------------------------------------------------------------------------
  async function fetchProduct(handle) {
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  if (!customElements.get('quick-add')) {
    customElements.define(
      'quick-add',
      class QuickAdd extends HTMLElement {
        connectedCallback() {
          this.trigger = this.querySelector('button');
          if (!this.trigger || this.trigger.dataset.quickAddBound) return;
          this.trigger.dataset.quickAddBound = 'true';
          this.trigger.addEventListener('click', (e) => this.onClick(e));
        }

        async onClick(e) {
          e.preventDefault();
          e.stopPropagation();
          const handle = this.dataset.productHandle;
          if (!handle) return;

          const product = await fetchProduct(handle);
          const variants = product?.variants || [];
          if (!variants.length) return;

          if (variants.length === 1) {
            addToCart(String(variants[0].id), { productId: product.id });
          } else {
            openDrawerFromProduct(product);
          }
        }
      }
    );
  }

  // Lo justo para los contextos que traen su propia sheet de tallas: el alta, con
  // sus secciones de carrito, su aviso por PUB_SUB y su minicart. Ver la cabecera.
  window.quickAddCart = { addToCart };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
